import { useEffect, useState } from 'react';
import {
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  Table,
  Badge,
  Loader,
  Alert,
  Select,
  Tabs,
  Progress,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBrain,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconChartBar,
  IconList,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

import { getModels, getCoefficients, trainAllModels } from '../api/prediction-api';
import type { PredictionModel, ModelCoefficient, TrainAllModelsResponse } from '../types/prediction-types';

const TARGET_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  orders: 'Orders',
  items: 'Items',
  preparation_time: 'Prep Time',
};

const TARGET_COLORS: Record<string, string> = {
  revenue: 'green',
  orders: 'blue',
  items: 'orange',
  preparation_time: 'grape',
};

function ModelCard({ model, isSelected, onClick }: { 
  model: PredictionModel; 
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <Card 
      shadow="sm" 
      padding="md" 
      radius="md" 
      withBorder
      style={{ 
        cursor: 'pointer',
        borderColor: isSelected ? '#228be6' : undefined,
        borderWidth: isSelected ? 2 : 1,
      }}
      onClick={onClick}
    >
      <Group justify="space-between" mb="xs">
        <Badge color={TARGET_COLORS[model.target_variable] || 'gray'} size="lg">
          {TARGET_LABELS[model.target_variable] || model.target_variable}
        </Badge>
        <Badge color={model.status === 'active' ? 'green' : 'gray'} variant="light" size="sm">
          {model.status}
        </Badge>
      </Group>

      <Stack gap="xs">
        <div>
          <Text size="xs" c="dimmed">Model Performance (R²)</Text>
          <Group gap="xs" align="center">
            <Progress 
              value={model.r_squared * 100} 
              size="lg" 
              style={{ flex: 1 }}
              color={model.r_squared > 0.3 ? 'green' : model.r_squared > 0.15 ? 'yellow' : 'red'}
            />
            <Text size="sm" fw={600} w={50}>
              {(model.r_squared * 100).toFixed(1)}%
            </Text>
          </Group>
        </div>

        <Group grow>
          <div>
            <Text size="xs" c="dimmed">RMSE</Text>
            <Text size="sm" fw={500}>{model.rmse.toFixed(2)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">MAE</Text>
            <Text size="sm" fw={500}>{model.mae.toFixed(2)}</Text>
          </div>
        </Group>

        <Group grow>
          <div>
            <Text size="xs" c="dimmed">Observations</Text>
            <Text size="sm" fw={500}>{model.total_observations}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Games</Text>
            <Text size="sm" fw={500}>{model.total_games}</Text>
          </div>
        </Group>

        <div>
          <Text size="xs" c="dimmed">Training Period</Text>
          <Text size="sm">
            {dayjs(model.training_start_date).format('MMM D, YYYY')} - {dayjs(model.training_end_date).format('MMM D, YYYY')}
          </Text>
        </div>

        <div>
          <Text size="xs" c="dimmed">Created</Text>
          <Text size="sm">{dayjs(model.created_at).format('MMM D, YYYY h:mm A')}</Text>
        </div>
      </Stack>
    </Card>
  );
}

function CoefficientTable({ coefficients }: { coefficients: ModelCoefficient[] }) {
  // Group by feature_group
  const grouped = coefficients.reduce((acc, coef) => {
    const group = coef.feature_group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(coef);
    return acc;
  }, {} as Record<string, ModelCoefficient[]>);

  // Sort groups
  const groupOrder = ['intercept', 'slot', 'log_transform', 'continuous', 'boolean', 'source', 'other'];
  const sortedGroups = Object.keys(grouped).sort(
    (a, b) => groupOrder.indexOf(a) - groupOrder.indexOf(b)
  );

  // Find max absolute coefficient for importance visualization
  const maxAbsCoef = Math.max(
    ...coefficients
      .filter(c => c.feature_name !== 'intercept')
      .map(c => Math.abs(c.coefficient))
  );

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Feature</Table.Th>
          <Table.Th>Group</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>Coefficient</Table.Th>
          <Table.Th>Importance</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sortedGroups.map(group => 
          grouped[group]
            .sort((a, b) => Math.abs(b.coefficient) - Math.abs(a.coefficient))
            .map(coef => {
              const importance = coef.feature_name === 'intercept' 
                ? 0 
                : (Math.abs(coef.coefficient) / maxAbsCoef) * 100;
              
              return (
                <Table.Tr key={coef.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{coef.feature_name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {coef.feature_group}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text 
                      size="sm" 
                      fw={500}
                      c={coef.coefficient > 0 ? 'green' : coef.coefficient < 0 ? 'red' : undefined}
                    >
                      {coef.coefficient >= 0 ? '+' : ''}{coef.coefficient.toFixed(4)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {coef.feature_name !== 'intercept' && (
                      <Progress
                        value={importance}
                        size="sm"
                        color={coef.coefficient > 0 ? 'green' : 'red'}
                        style={{ width: 100 }}
                      />
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })
        )}
      </Table.Tbody>
    </Table>
  );
}

export default function ModelsPage() {
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<PredictionModel[]>([]);
  const [coefficients, setCoefficients] = useState<ModelCoefficient[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [modelsData, coefficientsData] = await Promise.all([
        getModels({ status: 'active' }),
        getCoefficients(),
      ]);
      setModels(modelsData.results || []);
      setCoefficients(coefficientsData.results || []);
      
      // Auto-select first target if none selected
      if (!selectedTarget && modelsData.results?.length > 0) {
        setSelectedTarget(modelsData.results[0].target_variable);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTrainAll = async () => {
    setTraining(true);
    try {
      const response: TrainAllModelsResponse = await trainAllModels();

      const successCount = [
        response.training.revenue.success,
        response.training.orders.success,
        response.training.items.success,
        response.training.preparation_time.success,
      ].filter(Boolean).length;

      if (successCount === 4) {
        notifications.show({
          title: 'Training Complete',
          message: 'All 4 models trained successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        notifications.show({
          title: 'Training Partial Success',
          message: `${successCount}/4 models trained successfully`,
          color: 'yellow',
          icon: <IconAlertCircle size={16} />,
        });
      }

      await fetchData();
    } catch (err) {
      notifications.show({
        title: 'Training Failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setTraining(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter coefficients by selected target
  const filteredCoefficients = selectedTarget
    ? coefficients.filter(c => c.target_variable === selectedTarget)
    : coefficients;

  const selectedModel = models.find(m => m.target_variable === selectedTarget);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Models & Coefficients</Title>
          <Text c="dimmed" size="sm">
            View trained models and their feature coefficients
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconBrain size={16} />}
            onClick={handleTrainAll}
            loading={training}
          >
            Train All Models
          </Button>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchData}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">Loading models...</Text>
        </Stack>
      ) : (
        <Tabs defaultValue="models">
          <Tabs.List>
            <Tabs.Tab value="models" leftSection={<IconChartBar size={16} />}>
              Models Overview
            </Tabs.Tab>
            <Tabs.Tab value="coefficients" leftSection={<IconList size={16} />}>
              Coefficients
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="models" pt="md">
            {models.length === 0 ? (
              <Alert color="yellow" title="No Models">
                No trained models found. Click "Train All Models" to train new models.
              </Alert>
            ) : (
              <Group align="flex-start" grow>
                {models.map(model => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    isSelected={selectedTarget === model.target_variable}
                    onClick={() => setSelectedTarget(model.target_variable)}
                  />
                ))}
              </Group>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="coefficients" pt="md">
            <Stack gap="md">
              <Group>
                <Select
                  label="Select Model"
                  placeholder="Choose a model"
                  data={models.map(m => ({
                    value: m.target_variable,
                    label: TARGET_LABELS[m.target_variable] || m.target_variable,
                  }))}
                  value={selectedTarget}
                  onChange={setSelectedTarget}
                  style={{ width: 200 }}
                />
                {selectedModel && (
                  <Paper p="sm" withBorder style={{ marginTop: 24 }}>
                    <Group gap="lg">
                      <div>
                        <Text size="xs" c="dimmed">R²</Text>
                        <Text fw={600}>{(selectedModel.r_squared * 100).toFixed(1)}%</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">RMSE</Text>
                        <Text fw={600}>{selectedModel.rmse.toFixed(2)}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Coefficients</Text>
                        <Text fw={600}>{filteredCoefficients.length}</Text>
                      </div>
                    </Group>
                  </Paper>
                )}
              </Group>

              {filteredCoefficients.length === 0 ? (
                <Alert color="blue" title="No Coefficients">
                  Select a model to view its coefficients.
                </Alert>
              ) : (
                <Card shadow="sm" padding={0} radius="md" withBorder>
                  <CoefficientTable coefficients={filteredCoefficients} />
                </Card>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  );
}
